import { Component, EventEmitter, Input, OnChanges, Output, signal } from '@angular/core';

import { EventResultFieldDef } from './models';
import { allResultFieldsFilled, ResultFieldValues } from './result-fields';

/** Who won, in terms of the two columns rather than of either caller's vocabulary. */
export type ResultFormOutcome = 'LEFT' | 'DRAW' | 'RIGHT';

/**
 * The shared result-entry control: who won, plus a value per configured result field
 * for each side.
 *
 * Deliberately neutral about WHICH two people it is describing. A player reports on
 * themselves and an opponent ('WIN'/'LOSS'); an organiser overrides on two named
 * participants ('A_WIN'/'B_WIN'). Both are the same form over the same data, so each
 * caller supplies its own labels and maps LEFT/RIGHT to its own outcome values.
 *
 * The winner is picked from a stacked radio list rather than three buttons across,
 * because a display name falls back to the raw userId (an email address) when the
 * registrant never set one -- which no third-of-a-row button can show without
 * truncating, and the organiser's view has two of them.
 */
@Component({
    standalone: false,
    selector: 'abs-event-result-form',
    template: `
        <div class="form event-result-form">
            @if (derivesOutcomeKey(); as key) {
                <!-- This event has a field that decides the winner from its own two values
                     (outcomeMode 'derives_outcome') -- there is nothing for a player to pick,
                     only to see, so no radio group is shown at all, in either mode. -->
                <div class="outcome-computed">
                    @if (outcome === 'LEFT') {
                        {{ leftWinLabel }}
                    } @else if (outcome === 'RIGHT') {
                        {{ rightWinLabel }}
                    } @else if (outcome === 'DRAW') {
                        {{ drawLabel }}
                    }
                </div>
            } @else {
                <ion-radio-group class="outcome-picker" [class.read-only]="readOnly" [value]="outcome" (ionChange)="chooseOutcome($event)">
                    <ion-item lines="full">
                        <ion-radio value="LEFT" justify="start" labelPlacement="end" [disabled]="readOnly">{{ leftWinLabel }}</ion-radio>
                    </ion-item>
                    <ion-item lines="full">
                        <ion-radio value="DRAW" justify="start" labelPlacement="end" [disabled]="readOnly">{{ drawLabel }}</ion-radio>
                    </ion-item>
                    <ion-item lines="full">
                        <ion-radio value="RIGHT" justify="start" labelPlacement="end" [disabled]="readOnly">{{ rightWinLabel }}</ion-radio>
                    </ion-item>
                </ion-radio-group>
            }

            <div class="result-fields-grid">
                <!-- Column order matches the radio list above it: left side first, the field
                     label in the middle, right side last. -->
                <div class="result-fields-header">
                    <span>{{ leftLabel }}</span>
                    <span></span>
                    <span>{{ rightLabel }}</span>
                </div>
                <!-- Read-only renders plain text rather than a disabled input. ion-input does not
                     propagate a changed readonly property to its inner native input once rendered,
                     so a form switching mode in place kept uneditable boxes; swapping the element
                     sidesteps that, and a value the user cannot change should not look like a
                     field anyway. -->
                @for (row of rows(); track row.key) {
                    <div class="result-fields-row">
                        @if (readOnly) {
                            <div class="result-field-value">{{ leftValues[row.key] }}</div>
                        } @else {
                            <ion-input type="number" [(ngModel)]="leftValues[row.key]" (ngModelChange)="emitValid()"></ion-input>
                        }
                        <label>{{ row.label }}</label>
                        @if (readOnly) {
                            <div class="result-field-value">{{ rightValues[row.key] }}</div>
                        } @else {
                            <ion-input type="number" [(ngModel)]="rightValues[row.key]" (ngModelChange)="emitValid()"></ion-input>
                        }
                    </div>
                }
            </div>
        </div>
    `
})
export class EventResultFormComponent implements OnChanges {
    @Input() resultFields: EventResultFieldDef[] = [];

    /** Show the values without allowing edits -- used when reviewing what an opponent reported. */
    @Input() readOnly = false;

    /** Column headers -- who each side of the grid is. */
    @Input() leftLabel = '';
    @Input() rightLabel = '';

    /** Radio labels. Supplied whole, not built from the column labels, so each caller
     *  can phrase them correctly ("You win" vs "Alice wins"). */
    @Input() leftWinLabel = '';
    @Input() rightWinLabel = '';
    @Input() drawLabel = '';

    /** Mutated in place by ngModel, so the caller reads its own object back on submit. */
    @Input() leftValues: ResultFieldValues = {};
    @Input() rightValues: ResultFieldValues = {};

    @Input() outcome: ResultFormOutcome | null = null;
    @Output() outcomeChange = new EventEmitter<ResultFormOutcome | null>();

    /** Whether an outcome is chosen and every field has a value on both sides. */
    @Output() validChange = new EventEmitter<boolean>();

    /** Field id resolved to its string key here rather than in the template, per the
     *  workspace rule against calling plain methods from templates. */
    readonly rows = signal<{ key: string; label: string }[]>([]);

    /** String key of the field that decides the winner itself (outcomeMode
     *  'derives_outcome'), or null when the event has none -- in which case the winner
     *  is picked from the radio group instead. At most one such field can exist
     *  (resultFieldsAreValid), so there is never a choice between two to make. */
    readonly derivesOutcomeKey = signal<string | null>(null);

    ngOnChanges() {
        const fields = this.resultFields ?? [];
        const derivesField = fields.find((field) => field.outcomeMode === 'derives_outcome');
        this.derivesOutcomeKey.set(derivesField?.id != null ? String(derivesField.id) : null);

        // A from_outcome field's value comes from the server once the outcome is known --
        // an input the server ignores is worse than no input -- so it gets no row at all
        // while editing. Read-only views (reviewing a stored result) still show it: by
        // then the server has already filled it in, and it is useful to see.
        this.rows.set(
            fields
                .filter((field) => this.readOnly || field.outcomeMode !== 'from_outcome')
                .map((field) => ({ key: String(field.id), label: field.label }))
        );
        this.emitValid();
    }

    chooseOutcome(event: any) {
        this.outcome = event?.detail?.value ?? null;
        this.outcomeChange.emit(this.outcome);
        this.emitValid();
    }

    /** LEFT/RIGHT by whoever reported the higher number on the derives-outcome field,
     *  DRAW when equal, or null until both sides have a value to compare. */
    private computeDerivedOutcome(key: string): ResultFormOutcome | null {
        const left = this.leftValues[key];
        const right = this.rightValues[key];
        if (left == null || right == null) {
            return null;
        }
        if (left === right) {
            return 'DRAW';
        }
        return left > right ? 'LEFT' : 'RIGHT';
    }

    emitValid() {
        const derivesKey = this.derivesOutcomeKey();
        if (derivesKey != null) {
            const computed = this.computeDerivedOutcome(derivesKey);
            if (computed !== this.outcome) {
                this.outcome = computed;
                this.outcomeChange.emit(this.outcome);
            }
        }
        const complete = allResultFieldsFilled(this.resultFields ?? [], this.leftValues, this.rightValues);
        this.validChange.emit(!!this.outcome && complete);
    }
}
